import { Injectable } from '@angular/core';
import { ConfigManagerService } from '@xpo-ltl/config-manager';
import { User } from '@xpo-ltl/sdk-common';
import * as _ from 'lodash';
import { ConfigManagerProperties } from '@enums/config-manager-properties.enum';
import { BehaviorSubject } from 'rxjs';
import { RamRolesOptions } from '@enums/user-role.enum';
import { AppRoutes } from '@enums/app-routes.enum';
import { Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class UserRoleService {
  user: User;
  ramRoles = new BehaviorSubject<string[]>(undefined);
  ramRoles$ = this.ramRoles.asObservable();
  allUserRamRoles: string[] = [];
  private isProduction = false;
  userLoaded$ = new BehaviorSubject<boolean>(false);
  private readonly removeDomainRegExp = /.*\//gm;

  constructor(
    private configManagerService: ConfigManagerService,
    private router: Router
  ) {
    this.isProduction = this.configManagerService.getSetting<boolean>(ConfigManagerProperties.production);

    this.ramRoles$.subscribe((ramRoles: string[]) => {
      this.allUserRamRoles = ramRoles;
    });
  }

  isAuthorizedUser(): boolean {
    return this.isObiAuditor() || this.isObiBuilder() || this.isObiViewer() || this.isTechTeam();
  }

  getRamRoles(userRoles: string[]): string[] {
    return _.map(userRoles, (role: string) => {
      return role.replace(this.removeDomainRegExp, '');
    });
  }

  routeBasedOnPermissions() {
    if (this.isCurrentRouteValid()) {
      // Stay on the same detail route & refresh if not on the list page
      this.router.navigateByUrl(this.router.url);
      return;
    } else if (this.isObiBuilder()) {
      this.router.navigate([`/${AppRoutes.OBI_AGREEMENT}/${AppRoutes.LIST_PAGE}`]);
    } else if (this.isObiAuditor() || this.isObiViewer()) {
      this.router.navigate([`/${AppRoutes.OFF_BILL_INCENTIVE}/${AppRoutes.LIST_PAGE}`]);
    } else {
      this.router.navigate([AppRoutes.NOT_AUTHORIZED_PAGE]);
    }
  }

  private isCurrentRouteValid(): boolean {
    const currentPath = this.getCurrentPath();
    const allowedRoutes = this.getAllowedRoutes();

    return allowedRoutes.some((route) => {
      return currentPath.startsWith(route) && !currentPath.includes(AppRoutes.LIST_PAGE);
    });
  }

  private getCurrentPath(): string {
    return this.router.url.split('?')[0];
  }

  private getAllowedRoutes(): string[] {
    if (this.isAuthorizedUser()) {
      return [`/${AppRoutes.OBI_AGREEMENT}`, `/${AppRoutes.OFF_BILL_INCENTIVE}`];
    }
    return [];
  }

  private isObiAuditor(): boolean {
    return this.allUserRamRoles
      ? this.isProduction
        ? this.allUserRamRoles.includes(RamRolesOptions.Auditor.text) || this.isTechTeam()
        : this.allUserRamRoles.includes(RamRolesOptions.AuditorTest.text) || this.isTechTeam()
      : false;
  }

  private isObiBuilder(): boolean {
    return this.allUserRamRoles
      ? this.isProduction
        ? this.allUserRamRoles.includes(RamRolesOptions.Builder.text) || this.isTechTeam()
        : this.allUserRamRoles.includes(RamRolesOptions.BuilderTest.text) || this.isTechTeam()
      : false;
  }

  private isObiViewer(): boolean {
    return this.allUserRamRoles
      ? this.isProduction
        ? this.allUserRamRoles.includes(RamRolesOptions.Viewer.text) || this.isTechTeam()
        : this.allUserRamRoles.includes(RamRolesOptions.ViewerTest.text) || this.isTechTeam()
      : false;
  }

  private isTechTeam() {
    return this.allUserRamRoles
      ? this.isProduction
        ? this.allUserRamRoles.includes(RamRolesOptions.ITSupport.text)
        : this.allUserRamRoles.includes(RamRolesOptions.ITSupportTest.text)
      : false;
  }

  get isAllowedToEditAgreement(): boolean {
    return this.isObiBuilder();
  }

  get isAllowedToEditAgreementEmail(): boolean {
    return this.isObiBuilder() || this.isObiAuditor();
  }

  get isAllowedToEditIncentive(): boolean {
    return this.isObiAuditor();
  }
}
